import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import FormBuilder from '../../../../../../shared/components/form-builder/form-builder';
import { IAdditionalInfo } from '../../../../tc-models/tc-req-dto/tc-certified-product-details.req_dto';
import { useTransactionCertificateStore } from '../../../../../../store/transaction-certificates.store';

interface AdditionalInfoProps {
  additionalInfoData: IAdditionalInfo;
  setAdditionalInfoData: React.Dispatch<React.SetStateAction<IAdditionalInfo>>;
  formRef: React.Ref<any>;
}

const AdditionalInfo: React.FC<AdditionalInfoProps> = ({
  additionalInfoData,
  setAdditionalInfoData,
  formRef,
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { getTCStandardGOTS } = useTransactionCertificateStore();

  const toggleDrawer = () => {
    if (drawerOpen) resetForm();
    setDrawerOpen((prev) => !prev);
  };

  const resetForm = () => {
    setAdditionalInfoData(additionalInfoData);
  };

  const formConfig = {
    pageName: "certified-products",
    subPageName: "tcProductAdditionalInfo",
    initialValues: additionalInfoData,
    validationSchema: Yup.object().shape({
      tcProductInfoFiberLength: Yup.string()
      .when([], {
          is: () => getTCStandardGOTS, // If false, apply required validation
          then: Yup.string().required("Fiber Length is required"),
          otherwise: Yup.string().nullable(), // Allow it to be nullable if GOTS is included
      })
      .max(255, "Maximum 255 Characters is allowed"), // Always enforce max length  
      tcProductInfoYarnCount: Yup.string()
      .nullable()
      .max(255, "Maximum 255 Characters is allowed"),
      tcProductInfoFabricWeightAndConstruct: Yup.string()
        .nullable()
        .max(255, "Maximum 255 Characters is allowed"),
      tcProductInfoFabricWeightOfFinishedProduct: Yup.string()
        .nullable()
        .max(255, "Maximum 255 Characters is allowed"),
      tcProductInfoTrimsExcluded: Yup.string().nullable().max(255, "Maximum 255 Characters is allowed"),
      tcProductInfoAdditional: Yup.string().nullable(),
      tcProductAdditivesWeight: Yup.string()
        .nullable()
        .test(
          "is-exactly-two-decimal-places",
          "Number must be a valid decimal number with up to 2 decimal places.",
          (value: string | null | undefined) => {
            if (!value) return true;
            return /^(0|[0-9]\d*|0\.\d{1,2}|[0-9]\d*\.\d{1,2})$/.test(value);
          }
        )
  
        .max(255, "Maximum 255 Characters is allowed"),
    }),
    sections: [
      {
        title: 'Additional Information',
        fields: [
          {
            label: 'Fiber Length (mm)',
            name: 'tcProductInfoFiberLength',
            type: 'text',
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: getTCStandardGOTS ? true :false,
            value: additionalInfoData.tcProductInfoFiberLength,
            props: {
              placeholder: 'Enter Fiber Length',
            },
          },
          {
            label: 'Yarn Count',
            name: 'tcProductInfoYarnCount',
            type: 'text',
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: false,
            value: additionalInfoData.tcProductInfoYarnCount,
            props: {
              placeholder: 'Enter Yarn Count',
            },
          },
          {
            label: 'Fabric Weight and Construct (kg)',
            name: 'tcProductInfoFabricWeightAndConstruct',
            type: 'text',
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: false,
            value: additionalInfoData.tcProductInfoFabricWeightAndConstruct,
            props: {
              placeholder: 'Enter Fabric Weight and Construct',
            },
          },
          {
            label: 'Fabric Weight of Finished Product (kg)',
            name: 'tcProductInfoFabricWeightOfFinishedProduct',
            type: 'text',
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: false,
            value: additionalInfoData.tcProductInfoFabricWeightOfFinishedProduct,
            props: {
              placeholder: 'Enter Fabric Weight of Finished Product',
            },
          },
          {
            label: 'Trims Excluded',
            name: 'tcProductInfoTrimsExcluded',
            type: 'text',
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: false,
            value: additionalInfoData.tcProductInfoTrimsExcluded,
            props: {
              placeholder: 'Enter Trims Excluded',
            },
          },
          {
            label: 'Additional Info',
            name: 'tcProductInfoAdditional',
            type: 'text',
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: false,
            value: additionalInfoData.tcProductInfoAdditional,
            props: {
              placeholder: 'Enter Additional Info',
            },
          },
          {
            label: 'Product Additives',
            name: 'tcProductAdditives',
            type: 'chips',
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: false,
            value: additionalInfoData.tcProductAdditives,
            props: {
              placeholder: 'Enter Product Additives',
            },
          },
          {
            label: 'Product Additives Weight (kg)',
            name: 'tcProductAdditivesWeight',
            type: 'number',
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: false,
            value: additionalInfoData.tcProductAdditivesWeight,
            props: {
              type: 'number',
              placeholder: 'Enter Product Additives Weight',
            },
          },
        ],
      },
    ],
    handleSubmit: (values: any) => {
      setAdditionalInfoData(values);
      toggleDrawer();
    },
  };

  return (
    <>
      <FormBuilder formConfig={formConfig} onSubmit={formConfig.handleSubmit} formRef={formRef} />
    </>
  );
};

export default AdditionalInfo;
